/**
 * @file Адаптер протокола загрузки: временные части, сборка и очистка.
 *
 * preflight читает локальные снимки папки и квоты. Создание части и запуск
 * сборки не повторяются автоматически, поскольку ответ может потеряться после записи.
 */
import { b64Encode } from '../../services/encoding.ts'
import { makeTunnelRequest } from '../../services/request.ts'
import { isTaskId } from '../../services/taskId.ts'
import {
    cachedAvailableTraffic,
    cachedFolderNames,
} from '../transfers/snapshots.ts'
import type { UploadHandle } from './engine.ts'
import { UploadError, checkUploadResponse } from './model.ts'
import type { SegmentRange, UploadRequest } from './model.ts'
import { waitForUploadTask } from './task.ts'

export function tunnel(
    request: Pick<UploadRequest, 'boxId' | 'token'>,
    path: string,
    opts: RequestInit,
    signal: AbortSignal,
) {
    return makeTunnelRequest({
        path,
        boxID: request.boxId,
        token: request.token,
        opts: {
            ...opts,
            signal: AbortSignal.any([signal, AbortSignal.timeout(60_000)]),
        },
    })
}

export async function preflight(
    request: UploadRequest,
    signal: AbortSignal,
): Promise<number> {
    signal.throwIfAborted()
    const available = cachedAvailableTraffic(request.boxId)
    if (available === null) throw new UploadError('quotaCheck')

    const names = cachedFolderNames(request.boxId, request.folder)
    if (!names) throw new UploadError('protocol')

    if (names.has(request.file.name)) throw new UploadError('conflict')

    return available
}

export function parseUploadLocation(location: string | null): UploadHandle {
    if (!location) throw new UploadError('protocol')

    let url: URL

    try {
        url = new URL(location, 'https://upload.invalid')
    } catch {
        throw new UploadError('protocol')
    }

    const match = url.pathname.match(/^\/files\/([A-Za-z0-9_~.-]+)$/)
    if (!match || ['.', '..'].includes(match[1]) || url.search || url.hash)
        throw new UploadError('protocol')

    return { id: match[1], path: `/files/${match[1]}` }
}

export async function create(
    request: UploadRequest,
    range: SegmentRange,
    runId: string,
    signal: AbortSignal,
): Promise<UploadHandle> {
    const length = String(range.end - range.start)
    const name = `${request.folder.replace(/\/$/, '')}/${request.file.name}.upload-${runId}.part-${range.index + 1}`
    const response = await tunnel(
        request,
        '/files',
        {
            method: 'POST',
            headers: {
                'Upload-Length': length,
                'X-Upload-Length': length,
                'Upload-Metadata': `filename ${b64Encode(name)}`,
            },
        },
        signal,
    )
    checkUploadResponse(response)

    return parseUploadLocation(response.headers.get('Location'))
}

export async function finalize(
    request: UploadRequest,
    handles: UploadHandle[],
    signal: AbortSignal,
): Promise<void> {
    signal.throwIfAborted()
    let response: Response

    // Создание задачи сборки меняет сервер. При потере ответа нельзя просто
    // отправить POST ещё раз: первая задача могла уже начать читать части.
    try {
        response = await tunnel(
            request,
            '/files/concatenation',
            {
                method: 'POST',
                headers: {
                    'X-Upload-IDs': handles
                        .map((handle) => handle.id)
                        .join(','),
                    'X-Concat-Output': b64Encode(
                        `${request.folder.replace(/\/$/, '')}/${request.file.name}`,
                    ),
                },
            },
            signal,
        )
    } catch {
        throw new UploadError('finalizationUnknown', true)
    }

    if (!response.ok) {
        // Ошибка сервера могла прийти уже после принятия задачи сборки.
        if (response.status >= 500 || response.status === 408)
            throw new UploadError('finalizationUnknown', true)

        checkUploadResponse(response)
    }

    // X-Task-ID подтверждает приём задачи, а не готовность конечного файла.
    // Только опрос со статусом done разрешит engine перейти к успешной очистке.
    const id = response.headers.get('X-Task-ID')
    if (!isTaskId(id)) throw new UploadError('finalizationUnknown', true)

    const bounded = AbortSignal.any([signal, AbortSignal.timeout(5 * 60_000)])

    try {
        await waitForUploadTask(
            (pollSignal) =>
                tunnel(
                    request,
                    `/task/${id}`,
                    { cache: 'no-store' },
                    pollSignal,
                ),
            bounded,
        )
    } catch (error) {
        // Неизвестная ошибка чтения статуса также сохраняет части на сервере:
        // отсутствие подтверждения не доказывает, что сборка остановилась.
        if (error instanceof UploadError) throw error

        throw new UploadError('finalizationUnknown', true)
    }
}

export async function remove(
    request: UploadRequest,
    handle: UploadHandle,
): Promise<void> {
    const response = await tunnel(
        request,
        handle.path,
        { method: 'DELETE' },
        AbortSignal.timeout(5_000),
    )
    if (response.status !== 404) checkUploadResponse(response)
}
